<script lang="ts" setup>
// ================================
// IMPORTS
// ================================
import { computed, ref, watch } from "vue"
import { useI18n } from "vue-i18n"
import type { Folder } from "@modules/energy-manager/types/dashboard"
import { useEmConsoCompareStore } from "@modules/energy-manager/stores/em-conso-compare.store"
import { useRoute, useRouter } from "vue-router"

// ================================
// INTERNATIONALIZATION
// ================================
const { t } = useI18n({
    messages: {
        fr: {
            component: {
                dashboard_iot_header: {
                    title: "Analyse sur {scope}",
                    menu: "Choisir le périmètre d'analyse",
                    select_folder: "Sélectionner un dossier",
                    option_all: "Tout le parc",
                    option_folder: "Un dossier spécifique",
                },
            },
        },
    },
})

// ================================
// PROPS & EMITS
// ================================
const props = withDefaults(
    defineProps<{
        folder?: Folder | null
        loading?: boolean
        dropdownTitle?: string
    }>(),
    {
        folder: null,
        loading: false,
        dropdownTitle: "",
    }
)

const emit = defineEmits<{
    "update:folderId": [folderId: string | null]
}>()

// ================================
// COMPOSABLES
// ================================
const route = useRoute()
const router = useRouter()
const emConsoCompareStore = useEmConsoCompareStore()

// ================================
// ÉTAT
// ================================
const menu = ref()
const isModalFolderVisible = ref(false)
const currentOption = ref<string>(
    route.query?.folderId ? "option-1" : "option-0"
)

watch(
    () => route?.query?.folderId,
    (folderId) => {
        currentOption.value = folderId ? "option-1" : "option-0"
    }
)

watch(isModalFolderVisible, (visible) => {
    if (visible) {
        emConsoCompareStore.ensureBuildingsAndFoldersForDialog()
    }
})

// ================================
// DONNÉES CALCULÉES
// ================================
const getDropdownOptions = computed(() => [
    {
        title: t("component.dashboard_iot_header.option_all"),
        value: "option-0",
    },
    {
        title: t("component.dashboard_iot_header.option_folder"),
        value: "option-1",
    },
])

const getDropdownMenuModel = computed(() => {
    const options = getDropdownOptions.value
    const optionItems = options.map((option) => ({
        label: option.title,
        icon: currentOption.value === option.value ? "pi pi-check" : undefined,
        command: async () => {
            if (menu.value) {
                menu.value.hide?.()
            }
            await new Promise((resolve) => setTimeout(resolve, 50))
            onChangeChoice(option.value)
        },
    }))

    const selectFolderItem =
        currentOption.value === "option-1"
            ? [
                  { separator: true },
                  {
                      label: t("component.dashboard_iot_header.select_folder"),
                      icon: "pi pi-folder-open",
                      command: () => {
                          isModalFolderVisible.value = true
                      },
                  },
              ]
            : []

    return [
        {
            label:
                props.dropdownTitle || t("component.dashboard_iot_header.menu"),
            items: [...optionItems, ...selectFolderItem],
        },
    ]
})

// ================================
// INTERACTIONS
// ================================
function toggleMenu(event: Event): void {
    menu.value?.toggle?.(event)
}

async function onChangeChoice(value: string): Promise<void> {
    currentOption.value = value

    if (value === "option-0" && route.query.folderId) {
        await router.push({ path: route.path })
        setTimeout(() => emit("update:folderId", null), 200)
        return
    }

    if (value === "option-1") {
        setTimeout(() => {
            isModalFolderVisible.value = true
        }, 200)
    }
}

async function onSelectFolder(payload: string | string[]): Promise<void> {
    const folderId = Array.isArray(payload) ? payload[0] : payload
    if (!folderId) return

    await router.push({
        path: route.path,
        query: { folderId },
    })

    setTimeout(() => {
        emit("update:folderId", folderId)
        isModalFolderVisible.value = false
    }, 200)
}
</script>

<template>
    <div class="flex items-center gap-2">
        <h1 class="text-2xl font-semibold text-primary-700">
            {{
                t("component.dashboard_iot_header.title", {
                    scope: folder?.name
                        ? `le dossier ${folder.name}`
                        : "tout le parc",
                })
            }}
        </h1>

        <div v-if="!loading" class="relative">
            <BaseButton
                text
                size="small"
                icon="pi pi-chevron-down"
                icon-pos="right"
                @click="toggleMenu"
                aria-haspopup="true"
                aria-controls="dropdown-folder"
            />
            <BaseMenu
                ref="menu"
                id="dropdown-folder"
                :model="getDropdownMenuModel"
                :popup="true"
            />
        </div>

        <FolderDialog
            v-model:visible="isModalFolderVisible"
            :folders="emConsoCompareStore.folderTreeList"
            :loading="emConsoCompareStore.fetchBuildingsAndFolders.isFetching"
            @select:folder="onSelectFolder"
        />
    </div>
</template>
